import * as React from 'react'
import { useNavigate } from 'react-router-dom'
import { useClients, useCreateJob } from '@/lib/queries.js'
import { fullName } from '@/lib/utils.js'
import { humanise } from '@/lib/format.js'
import { Button } from '@/components/ui/button.jsx'
import { Field, Label } from '@/components/ui/field.jsx'
import { Select, Textarea } from '@/components/ui/select.jsx'
import { Dialog, DialogContent, DialogFooter } from '@/components/ui/dialog.jsx'
import { Alert } from '@/components/ui/feedback.jsx'
import { ERR } from '@/lib/errors.js'

const JOB_TYPES = [
  'kitchen', 'bathroom', 'flooring', 'whole_home', 'basement', 'roofing',
  'hvac', 'plumbing', 'electrical', 'general', 'custom'
]

/**
 * Create a job (the opportunity record).
 *
 * ── JOB-FIRST, AND WHY THE FORM SAYS SO ─────────────────────────────────────
 * Phase 1 D1 settled that a job is created before an estimate can exist
 * (`estimates.job_id` is NOT NULL). A job therefore needs a client, and this
 * form makes that a required choice rather than discovering it as a 400.
 *
 * The job lands in the entry stage automatically — the API defaults it to the
 * lowest `stage_order` when none is given, so there is no stage picker here.
 * Choosing a stage at creation would let someone start a job in "Won".
 */
export function NewJobDialog ({ open, onOpenChange, clientId }) {
  const navigate = useNavigate()
  const clientsQuery = useClients({ limit: 200 })
  const createJob = useCreateJob()

  const [form, setForm] = React.useState({
    client_id: clientId ?? '', title: '', job_type: 'custom', description: ''
  })
  const [error, setError] = React.useState(null)

  React.useEffect(() => {
    if (open) {
      setForm({ client_id: clientId ?? '', title: '', job_type: 'custom', description: '' })
      setError(null)
    }
  }, [open, clientId])

  const set = (key, value) => setForm(f => ({ ...f, [key]: value }))

  async function handleSubmit (event) {
    event.preventDefault()
    setError(null)
    try {
      const { job } = await createJob.mutateAsync({
        client_id: form.client_id,
        title: form.title.trim(),
        job_type: form.job_type,
        description: form.description.trim() || undefined
      })
      onOpenChange(false)
      navigate(`/jobs/${job.id}`)
    } catch (err) {
      setError(err)
    }
  }

  const clients = clientsQuery.data ?? []
  const noClients = !clientsQuery.isPending && clients.length === 0

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        title="New job"
        description="A job is the opportunity — estimates, appointments and invoices all hang off it."
      >
        <form id="new-job-form" onSubmit={handleSubmit} className="space-y-4" noValidate>
          {error && (
            <Alert tone="danger">
              {/* Field-level slugs get named so the message points at the fix
                  rather than restating that something went wrong. */}
              {error.slug === ERR.MISSING_FIELD
                ? 'Choose a client and give the job a title.'
                : error.message}
            </Alert>
          )}

          {noClients
            ? (
              <Alert tone="info" title="No clients yet">
                A job belongs to someone. Add a client first, then come back —
                or let your lead form create both automatically.
              </Alert>
              )
            : (
              <div className="space-y-1.5">
                <Label htmlFor="job-client">Client</Label>
                <Select
                  id="job-client"
                  value={form.client_id}
                  onChange={e => set('client_id', e.target.value)}
                  required
                  disabled={Boolean(clientId)}
                >
                  <option value="">Choose a client…</option>
                  {clients.map(c => (
                    <option key={c.id} value={c.id}>{fullName(c)}</option>
                  ))}
                </Select>
              </div>
              )}

          <Field
            id="job-title"
            label="What is the work?"
            placeholder="Kitchen remodel"
            value={form.title}
            onChange={e => set('title', e.target.value)}
            hint="This is what you will see on the board."
            required
          />

          <div className="space-y-1.5">
            <Label htmlFor="job-type">Type</Label>
            <Select id="job-type" value={form.job_type} onChange={e => set('job_type', e.target.value)}>
              {JOB_TYPES.map(t => <option key={t} value={t}>{humanise(t)}</option>)}
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="job-description">Notes</Label>
            <Textarea
              id="job-description"
              value={form.description}
              onChange={e => set('description', e.target.value)}
              placeholder="Anything worth remembering before the first visit."
            />
          </div>
        </form>

        <DialogFooter className="-mx-5 -mb-4 mt-4">
          <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="new-job-form"
            loading={createJob.isPending}
            disabled={noClients || !form.client_id || !form.title.trim()}
          >
            Create job
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
